import { Stack } from "@octopus-energy/coral/atoms"
import { QueryClient } from "@tanstack/react-query"
import { type GetStaticPropsContext } from "next"
import CustomHead from "@/components/atoms/CustomHead"
import { Page } from "@/components/organisms"
import { FaqHelpCard } from "@/features/customer-service"
import { fetchSbLinks, getStoryblokLayout, StoryTemplate } from "@/features/storyblok"
import { withStoryProps } from "@/features/storyblok/server"
import { type SbFaqArticleStory, type SbFaqCategoryStory } from "@/features/storyblok/types"

const SECTION_URL = "aide-faq"

type FaqPageProps = {
    story: SbFaqArticleStory | SbFaqCategoryStory
    preview: boolean
}

export default function FaqPage({ story, preview }: FaqPageProps) {
    return (
        <>
            <CustomHead
                title={"meta" in story.content ? story.content.meta?.title : story.content.title}
                description={"meta" in story.content ? story.content.meta?.description : undefined}
            />
            <Page maxWidthPreset="md">
                <Stack flexDirection="column">
                    <StoryTemplate
                        story={story}
                        preview={preview}
                    />

                    <FaqHelpCard />
                </Stack>
            </Page>
        </>
    )
}

FaqPage.getLayout = getStoryblokLayout
FaqPage.messages = [...getStoryblokLayout.messages, ...FaqHelpCard.messages] as const

export async function getStaticPaths() {
    const links = await fetchSbLinks({ starts_with: `${SECTION_URL}/` })

    return {
        paths: links.map(({ slug }) => `/${slug}`),
        fallback: true,
    }
}

export async function getStaticProps(context: GetStaticPropsContext) {
    const queryClient = new QueryClient()
    const slug = Array.isArray(context.params?.slug)
        ? `${SECTION_URL}/${context.params?.slug?.join("/")}`
        : ""

    return withStoryProps({
        slug,
        options: { resolve_relations: "false" },
        context,
        queryClient,
        intlNamespaces: FaqPage.messages,
        props: {},
    })
}
